import { useEffect, useRef, useState } from 'react';
import { callApi } from '../lib/api.ts';
import { fileToBase64 } from '../lib/fileToBase64.ts';
import { prepareImageUpload } from '../lib/imageDownscale.ts';import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid, sectionDesc } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';
import { FigmaConfirm } from './FigmaConfirm.tsx';
import type { FigmaMapResult, MappedTokens } from '../lib/figmaConfirm.ts';
import type { FontFace, TokenSchema } from '../lib/tokenSchema.ts';

/**
 * Brand-kit intake: drop the client's font files and reference artboards. Fonts
 * upload to the client's public/fonts/ and become typography.fontFaces entries;
 * artboards upload to docs/clients/<slug>/artboards/brand-kit/ and their paths are
 * recorded for the AI extraction (B2c). is_live-gated by the upload endpoints; the
 * UI also disables when locked/live.
 */
export function BrandIntake({
  clientId,
  disabled,
  fontFaces,
  onFontFacesChange,
  artboards,
  onArtboardsChange,
  onExtracted,
  onFigmaConfirmed,
}: {
  clientId: string;
  disabled: boolean;
  fontFaces: FontFace[];
  onFontFacesChange: (next: FontFace[]) => void;
  artboards: string[];
  onArtboardsChange: (next: string[]) => void;
  onExtracted: (tokens: TokenSchema) => void;
  // Half B: the designer confirmed a Figma mapping → merge this partial into the editor tokens
  // (the parent does the merge + the existing Lock). Co-equal with onExtracted (the AI path).
  onFigmaConfirmed: (mapped: MappedTokens) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractErr, setExtractErr] = useState<string | null>(null);
  const [extractDone, setExtractDone] = useState(false);
  // Figma (Neue) JSON design specs — the repo IS the catalogue (no DB), like references/assets, so
  // list the client's committed specs on load (figma-json-ingestion.md).
  const [designSpecs, setDesignSpecs] = useState<{ name: string; path: string }[]>([]);
  const [dsBusy, setDsBusy] = useState<string | null>(null);
  const [dsErr, setDsErr] = useState<string | null>(null);
  // Half B: map a stored Neue spec → the human-confirm step. `mapping` holds the mapper result for
  // the spec currently being confirmed (null = confirm panel closed).
  const [mapping, setMapping] = useState<{ name: string; result: FigmaMapResult } | null>(null);
  const [mapBusy, setMapBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void callApi<{ specs: { name: string; path: string }[] }>('design-spec', {
      method: 'POST',
      body: JSON.stringify({ clientId, action: 'list' }),
    })
      .then((r) => {
        if (!cancelled) setDesignSpecs(r.specs ?? []);
      })
      .catch(() => {
        /* best-effort — a list hiccup shouldn't break the Brand-Kit step */
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  async function extract() {
    setExtractErr(null);
    setExtractDone(false);
    setExtracting(true);
    try {
      const { buildId } = await callApi<{ buildId: string }>('brand-extract', {
        method: 'POST',
        body: JSON.stringify({ clientId, artboardPaths: artboards, fonts: fontFaces.map((f) => ({ family: f.family, src: f.src })) }),
      });
      // Poll until terminal (~4s cadence, well inside the background limit).
      const started = Date.now();
      for (;;) {
        await new Promise((r) => setTimeout(r, 4_000));
        const s = await callApi<{ status: string; proposedTokens: TokenSchema | null; error: string | null }>(
          'brand-extract-status',
          { method: 'POST', body: JSON.stringify({ buildId }) },
        );
        if (s.status === 'success' && s.proposedTokens) {
          onExtracted(s.proposedTokens);
          setExtractDone(true);
          break;
        }
        if (s.status === 'failed') throw new Error(s.error ?? 'Extraction failed.');
        if (Date.now() - started > 14 * 60_000) throw new Error('Extraction timed out.');
      }
    } catch (e) {
      console.error('[brand-extract]', e);
      setExtractErr(friendlyError(e, 'Extraction didn’t complete — please try again.'));
    } finally {
      setExtracting(false);
    }
  }

  async function uploadFonts(files: FileList | null) {
    if (!files || files.length === 0) return;
    setErr(null);
    const added: FontFace[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setBusy(`Uploading font ${i + 1}/${files.length}…`);
        const dataBase64 = await fileToBase64(files[i]);
        const up = await callApi<{ src: string; family: string }>('font-upload', {
          method: 'POST',
          body: JSON.stringify({ clientId, filename: files[i].name, dataBase64 }),
        });
        added.push({ family: up.family, src: up.src });
      }
      onFontFacesChange([...fontFaces, ...added]);
    } catch (e) {
      console.error('[font-upload]', e);
      setErr(friendlyError(e, 'Couldn’t add that font — please try again.'));
    } finally {
      setBusy(null);
    }
  }

  // The reference-images drop zone takes BOTH brand images AND a Figma (Neue) `.json` export (the
  // ONE accepted drop point for a design spec — figma-json-ingestion.md). Partition by extension:
  // images go to artboard-upload (vision), `.json` to the never-ship design-spec bucket.
  function isJsonFile(f: File): boolean {
    return /\.json$/i.test(f.name) || f.type === 'application/json';
  }

  function onReferenceFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    const specs = files.filter(isJsonFile);
    const images = files.filter((f) => !isJsonFile(f));
    if (images.length) void uploadArtboards(images);
    if (specs.length) void uploadDesignSpecs(specs);
  }

  async function uploadArtboards(files: File[]) {
    if (files.length === 0) return;
    setErr(null);
    const added: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setBusy(`Uploading reference ${i + 1}/${files.length}…`);
        // Downscale oversized images in the browser so vision extraction never
        // fails on a big drop (Anthropic caps at 8000px/side). The returned
        // filename's extension matches the (possibly re-encoded) bytes.
        const { base64, filename } = await prepareImageUpload(files[i]);
        const up = await callApi<{ path: string }>('artboard-upload', {
          method: 'POST',
          body: JSON.stringify({ clientId, sectionId: 'brand-kit', filename, dataBase64: base64 }),
        });
        added.push(up.path);
      }
      onArtboardsChange([...artboards, ...added]);
    } catch (e) {
      console.error('[artboard-upload]', e);
      setErr(friendlyError(e, 'Couldn’t add that image — please try again.'));
    } finally {
      setBusy(null);
    }
  }

  async function uploadDesignSpecs(files: File[]) {
    if (files.length === 0) return;
    setDsErr(null);
    try {
      for (let i = 0; i < files.length; i++) {
        setDsBusy(`Uploading design spec ${i + 1}/${files.length}…`);
        const dataBase64 = await fileToBase64(files[i]);
        await callApi('design-spec', {
          method: 'POST',
          body: JSON.stringify({ clientId, action: 'upload', files: [{ filename: files[i].name, dataBase64 }] }),
        });
      }
      // Re-list for the authoritative committed set (the repo IS the catalogue).
      const r = await callApi<{ specs: { name: string; path: string }[] }>('design-spec', {
        method: 'POST',
        body: JSON.stringify({ clientId, action: 'list' }),
      });
      setDesignSpecs(r.specs ?? []);
    } catch (e) {
      console.error('[design-spec]', e);
      setDsErr(friendlyError(e, 'Couldn’t add that design spec — it must be a Figma (Neue) JSON export.'));
    } finally {
      setDsBusy(null);
    }
  }

  // Half B: run the single-source mapper on a stored spec (server-side) → open the confirm step.
  async function mapDesignSpec(name: string) {
    setDsErr(null);
    setMapBusy(name);
    try {
      const result = await callApi<FigmaMapResult>('design-spec', {
        method: 'POST',
        body: JSON.stringify({ clientId, action: 'map', name }),
      });
      setMapping({ name, result });
    } catch (e) {
      console.error('[design-spec map]', e);
      setDsErr(friendlyError(e, 'Couldn’t read that design spec’s tokens — please try again.'));
    } finally {
      setMapBusy(null);
    }
  }

  async function removeDesignSpec(name: string) {
    setDsErr(null);
    try {
      await callApi('design-spec', { method: 'POST', body: JSON.stringify({ clientId, action: 'delete', name }) });
      setDesignSpecs((prev) => prev.filter((s) => s.name !== name));
    } catch (e) {
      console.error('[design-spec delete]', e);
      setDsErr(friendlyError(e, 'Couldn’t remove that design spec — please try again.'));
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-ink">Start from the client’s brand</h3>
        <p className={sectionDesc}>
          Drop their fonts and a few brand/reference images. Fonts are added below; the references
          feed the automatic extraction.
        </p>
      </div>

      {/* WOFF2-preferred advisory (amber — a nudge, not an error: TTF/OTF are still accepted). */}
      <div className="rounded border border-warning-bright bg-warning-bright/10 p-2.5 text-xs text-warning">
        <span className="font-semibold">WOFF2 is best</span> — the smallest, fastest web-font format.
        Convert TTF/OTF to WOFF2 before uploading where you can, for faster sites. WOFF, OTF and TTF
        are accepted too, but they’re heavier.
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <DropZone
          label="Font files"
          hint=".woff2 (preferred) · .woff · .otf · .ttf"
          accept=".woff2,.woff,.otf,.ttf"
          disabled={disabled || !!busy}
          onFiles={uploadFonts}
        />
        <DropZone
          label="Brand / reference images · Figma JSON"
          hint="PNG · JPG · WEBP · Figma (Neue) .json"
          accept="image/png,image/jpeg,image/gif,image/webp,.json,application/json"
          disabled={disabled || !!busy || !!dsBusy}
          onFiles={onReferenceFiles}
        />
      </div>

      {busy && <p className="text-xs text-info">{busy}</p>}
      {err && <p className="text-xs text-danger">{err}</p>}
      {dsBusy && <p className="text-xs text-info">{dsBusy}</p>}
      {dsErr && <p className="text-xs text-danger">{dsErr}</p>}

      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <button
          onClick={extract}
          disabled={disabled || extracting || !!busy || (artboards.length === 0 && fontFaces.length === 0)}
          className={btnSolid}
        >
          <FlapText>{extracting ? 'Extracting…' : 'Extract the brand kit'}</FlapText>
        </button>
        <span className="text-xs text-ink-2">
          Reads the references + fonts and fills the design system below — review &amp; lock it.
        </span>
        {extractDone && !extracting && (
          <span className="rounded bg-success-bright/10 px-2 py-0.5 text-xs font-medium text-success">
            ✓ Extracted — review &amp; lock below
          </span>
        )}
      </div>
      {extracting && (
        <p className="rounded border border-info bg-info-bright/10 p-2 text-xs text-info">
          Reading the references and writing the design system — this runs in the background
          (~1–3 min). Keep this step open until it fills in below.
        </p>
      )}
      {extractErr && <p className="text-xs text-danger">{extractErr}</p>}

      {fontFaces.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-ink-2">Fonts ({fontFaces.length})</p>
          <ul className="space-y-1">
            {fontFaces.map((f, i) => (
              <li key={`${f.src}-${i}`} className="flex items-center justify-between rounded bg-paper-2 px-2 py-1 text-xs">
                <span>
                  <span className="font-medium">{f.family}</span>{' '}
                  <span className="font-mono text-ink-2">{f.src}</span>
                </span>
                <button
                  onClick={() => onFontFacesChange(fontFaces.filter((_, j) => j !== i))}
                  disabled={disabled}
                  className="text-ink-2 hover:text-danger disabled:opacity-40"
                  aria-label={`Remove ${f.family}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {artboards.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-ink-2">Reference images ({artboards.length})</p>
          <ul className="space-y-1">
            {artboards.map((p, i) => (
              <li key={`${p}-${i}`} className="flex items-center justify-between rounded bg-paper-2 px-2 py-1 text-xs">
                <span className="truncate font-mono text-ink-2">{p.split('/').pop()}</span>
                <button
                  onClick={() => onArtboardsChange(artboards.filter((_, j) => j !== i))}
                  disabled={disabled}
                  className="text-ink-2 hover:text-danger disabled:opacity-40"
                  aria-label="Remove reference"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {designSpecs.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-ink-2">Figma design specs ({designSpecs.length})</p>
          <p className={`mb-1 ${sectionDesc} text-xs`}>
            Read at build as an exact artboard — pixel-exact layout, type &amp; tokens. Never shipped.
          </p>
          <ul className="space-y-1">
            {designSpecs.map((s) => (
              <li key={s.path} className="flex items-center gap-2 rounded bg-paper-2 px-2 py-1 text-xs">
                <span className="flex-1 truncate font-mono text-ink-2">{s.name}</span>
                {/* Half B: map this spec's tokens → the brand kit (the human-confirm step). */}
                <button
                  onClick={() => void mapDesignSpec(s.name)}
                  disabled={disabled || !!mapBusy}
                  className="rounded border border-line px-2 py-0.5 text-[11px] text-ink-2 hover:bg-paper disabled:opacity-40"
                >
                  {mapBusy === s.name ? 'Reading…' : 'Map to brand'}
                </button>
                <button
                  onClick={() => removeDesignSpec(s.name)}
                  disabled={disabled}
                  className="text-ink-2 hover:text-danger disabled:opacity-40"
                  aria-label={`Remove ${s.name}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Half B: the human-confirm step — Mapped + Unmapped, Apply merges into the editor tokens. */}
      {mapping && (
        <FigmaConfirm
          result={mapping.result}
          fileName={mapping.name}
          onConfirm={(mapped) => {
            onFigmaConfirmed(mapped);
            setMapping(null);
          }}
          onCancel={() => setMapping(null)}
        />
      )}
    </div>
  );
}

function DropZone({
  label,
  hint,
  accept,
  disabled,
  onFiles,
}: {
  label: string;
  hint: string;
  accept: string;
  disabled: boolean;
  onFiles: (files: FileList | null) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOver(false);
        onFiles(e.dataTransfer.files);
      }}
      onClick={() => !disabled && ref.current?.click()}
      className={`cursor-pointer rounded border border-dashed p-4 text-center text-xs ${
        disabled ? 'cursor-not-allowed border-line opacity-50' : over ? 'border-ink bg-paper-2' : 'border-line'
      }`}
    >
      <div className="font-medium text-ink-2">{label}</div>
      <div className="text-ink-2">drag &amp; drop or click · {hint}</div>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
